// Επιστροφή προκαταβολής ταμείου. Κάρτα: στο τερματικό — ο πελάτης φέρνει την κάρτα — και 8.5 από
// τον server. Μετρητά: από το συρτάρι. IRIS: δεν υποστηρίζεται ακόμη (ο server απαντά 409).
// Ίδιοι κανόνες κλειδιού/αναμονής με τη χρέωση (useChargeAttempt).

import React, { useMemo, useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { Btn, Field, Note, inputClass } from '../dineIn/floorEditor/ui';
import { depositsApi, type ChargeOutcome } from './api';
import { centsToApi, centsToInput, money, parseAmount, toCents } from './model';
import { Choice, OutcomeNote, ReceiptLine, Shell, Uncertain, Waiting, useDT } from './ui';
import { useChargeAttempt } from './useChargeAttempt';
import { pickTerminal, savedTerminalId, useActiveTerminals } from './useDepositsConfig';
import type { DepositView, RefundBody, RefundResponse } from './types';

type RefundMeta = { cents: number; terminalId: number | null };

/** Έλεγχος ποσού επιστροφής: θετικό, έως 2 δεκαδικά, όχι πάνω από το διαθέσιμο. */
export function refundAmountError(input: string, availableCents: number): 'amount' | 'max' | null {
  const cents = parseAmount(input);
  if (cents == null || cents <= 0) return 'amount';
  if (cents > availableCents) return 'max';
  return null;
}

export default function RefundModal({ storeId, deposit, onClose, onDone }: {
  storeId: number;
  deposit: DepositView;
  onClose: () => void;
  onDone?: (payment: DepositView | null) => void;
}) {
  const dt = useDT();
  const { employee } = useAuth();
  const memoId = `refund:${deposit.id}`;
  const attempt = useChargeAttempt<RefundResponse, RefundMeta>(memoId);
  const prev = attempt.resumed;
  const availableCents = toCents(deposit.available);
  const [amount, setAmount] = useState(() => centsToInput(prev ? prev.cents : availableCents));
  const [terminalId, setTerminalId] = useState<number | null>(prev ? prev.terminalId : null);
  const [touched, setTouched] = useState(false);

  const card = deposit.method === 'card';
  const iris = deposit.method === 'iris';
  const { terminals } = useActiveTerminals(storeId, card);
  const autoTerminal = useMemo(
    () => pickTerminal(terminals, [deposit.terminal_id, savedTerminalId(employee?.id, storeId)]),
    [terminals, deposit.terminal_id, employee?.id, storeId],
  );
  const termId = terminalId ?? autoTerminal;

  const cents = parseAmount(amount);
  const amountErr = refundAmountError(amount, availableCents);
  const errors = {
    amount: amountErr === 'amount' ? dt('deposits:err.amount') : amountErr === 'max' ? dt('deposits:err.amountMax', { amount: money(deposit.available) }) : null,
    terminal: card && !termId ? dt('deposits:err.terminal') : null,
  };
  const valid = !iris && !errors.amount && !errors.terminal;

  const { phase, outcome, locked, charging } = attempt;
  const done = phase === 'ok' && outcome?.kind === 'ok' ? outcome.data : null;

  const send = (clientKey: string): Promise<ChargeOutcome<RefundResponse>> => {
    const body: RefundBody = { amount: centsToApi(cents!), clientKey };
    if (card && termId) body.terminalId = termId;
    return depositsApi.refund(storeId, deposit.id, body).then(out => {
      if (out.kind !== 'ok') return out;
      const st = out.data.refund?.status;
      if (st === 'pending') return { kind: 'uncertain', message: null, code: 'pending', id: out.data.refund.id };
      if (st === 'failed') return { kind: 'declined', message: out.data.message || null, data: out.data };
      return out;
    });
  };

  const submit = async () => {
    if (charging || iris) return;
    if (phase !== 'uncertain' && !valid) { setTouched(true); return; }
    const out = await attempt.run(send, { id: memoId, meta: { cents: cents!, terminalId: card ? termId : null } });
    if (!out) return;
    if (out.kind === 'ok') onDone?.(out.data.payment || null);
    else if (out.kind === 'uncertain') onDone?.(null);
  };

  const who = deposit.customer_name || deposit.customer_phone || '';
  const sub = (
    <>
      {who && `${who} · `}{money(deposit.amount)} · {dt(`deposits:method.${deposit.method}`)}
      {deposit.receipt && deposit.receipt.number != null && ` · 8.4 ${[deposit.receipt.series, deposit.receipt.number].filter(Boolean).join('-')}`}
    </>
  );
  const title = dt('deposits:refundModal.title');

  if (done) {
    const r = done.refund;
    const refunded = r?.amount ?? (cents || 0) / 100;
    return (
      <Shell title={title} sub={sub} closable onClose={onClose} testId="refund-modal"
        footer={<Btn variant="primary" onClick={onClose}>{dt('deposits:close')}</Btn>}>
        <div role="status" data-testid="refund-ok" className="grid gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-4">
          <p className="flex items-center gap-2 text-[17px] font-bold text-emerald-700 dark:text-emerald-300">
            <CheckCircle className="h-6 w-6 shrink-0" />{dt('deposits:refundModal.okTitle', { amount: money(refunded) })}
          </p>
          {deposit.method === 'cash'
            ? <p className="text-[15px] font-semibold text-t1">{dt('deposits:refundModal.cashGive', { amount: money(refunded) })}</p>
            : <ReceiptLine doc="8.5" receipt={r?.receipt} pending={!r?.receipt?.mark} />}
          {done.payment && toCents(done.payment.available) > 0 && (
            <p className="text-[13px] text-t3">{dt('deposits:refundModal.left', { amount: money(done.payment.available) })}</p>
          )}
        </div>
      </Shell>
    );
  }

  const uncertainCode = outcome?.kind === 'uncertain' ? outcome.code : null;
  const footer = phase === 'uncertain' ? (
    <>
      <Btn onClick={onClose}>{dt('deposits:close')}</Btn>
      <Btn variant="primary" onClick={submit} data-testid="refund-check">{dt('deposits:modal.checkAgain')}</Btn>
    </>
  ) : (
    <>
      <Btn onClick={onClose} disabled={charging}>{dt('deposits:cancel')}</Btn>
      <Btn variant="danger" className="h-12 px-5 text-[15px]" disabled={charging || iris} onClick={submit} data-testid="refund-submit">
        {dt('deposits:refundModal.confirm', { amount: cents && cents > 0 ? money(cents / 100) : '' })}
      </Btn>
    </>
  );

  return (
    <Shell title={title} sub={sub} closable={!charging} onClose={onClose} footer={footer} testId="refund-modal">
      {charging && <Waiting title={card ? dt('deposits:refundModal.charging') : dt('deposits:refundModal.chargingCash')}
        hint={card ? dt('deposits:modal.chargingHint') : undefined} />}
      {phase === 'uncertain' && (
        <Uncertain title={card ? dt('deposits:modal.uncertain') : dt('deposits:modal.uncertainCash')}>
          {uncertainCode === 'pending' && <p>{dt('deposits:refundModal.pending')}</p>}
          {uncertainCode === 'resumed' && prev && <p>{dt('deposits:refundModal.resumed', { amount: money(prev.cents / 100) })}</p>}
          {(uncertainCode === 'network' || uncertainCode === 'timeout') && <p>{dt('deposits:modal.lost')}</p>}
          <p>{dt('deposits:modal.checkHint')}</p>
        </Uncertain>
      )}
      <OutcomeNote out={outcome} declinedTitle={dt('deposits:refundModal.declined')} />

      <div className="flex flex-wrap items-baseline justify-between gap-2 rounded-xl bg-surface-2 px-4 py-3">
        <span className="text-[13px] text-t3">{dt('deposits:refundModal.available')}</span>
        <span className="text-[20px] font-bold tabular-nums text-t1" data-testid="refund-available">{money(deposit.available)}</span>
      </div>

      {iris ? (
        <div data-testid="refund-iris-unsupported"><Note tone="warn">{dt('deposits:refundModal.irisUnsupported')}</Note></div>
      ) : (
        <fieldset disabled={locked} className="grid gap-4 disabled:opacity-60">
          <Field label={dt('deposits:refundModal.amount')} error={touched || amountErr === 'max' ? errors.amount : null}
            hint={dt('deposits:refundModal.max', { amount: money(deposit.available) })}>
            <input className={`${inputClass} h-12 text-[20px] font-bold tabular-nums`} inputMode="decimal" value={amount}
              onChange={e => setAmount(e.target.value)} data-testid="refund-amount" />
          </Field>
          {card ? (
            <>
              <Note>{dt('deposits:refundModal.cardHint')}</Note>
              <div role="group" aria-label={dt('deposits:modal.terminal')} className="grid gap-1.5">
                <span className="text-[13px] font-medium text-t2">{dt('deposits:modal.terminal')}</span>
                {terminals == null ? <div className="h-12 animate-pulse rounded-xl bg-surface-2" />
                  : terminals.length === 0 ? <Note tone="warn">{dt('deposits:modal.noTerminals')}</Note> : (
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2">
                      {terminals.map(tm => (
                        <Choice key={tm.id} on={termId === tm.id} onClick={() => setTerminalId(tm.id)} testId={`refund-terminal-${tm.id}`}>
                          {tm.terminal_name || tm.terminal_id}
                        </Choice>
                      ))}
                    </div>
                  )}
                {touched && errors.terminal && terminals && terminals.length > 0 && <span className="text-[12.5px] text-red-600 dark:text-red-400">{errors.terminal}</span>}
              </div>
            </>
          ) : (
            <Note>{dt('deposits:refundModal.cashHint')}</Note>
          )}
        </fieldset>
      )}
    </Shell>
  );
}
